import { useQuery, useMutation } from '@tanstack/react-query';
import { setUser } from '@/redux/actions/userActions';

const apiUrl = process.env.REACT_APP_API_URL;
const apiOauthUrl = process.env.REACT_APP_OAUTH_API_URL;
export const getOAuthToken = async ({ username, password }) => {
  const clientId = 'USER_CLIENT_APP';
  const clientSecret = 'password';

  const body = new URLSearchParams({
    username,
    password,
    grant_type: 'password',
  });

  const response = await fetch(`${apiOauthUrl}/oauth/token`, {
    method: 'POST',
    headers: {
      Authorization: 'Basic ' + btoa(`${clientId}:${clientSecret}`),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText);
  }

  return response.json();
};

export const getOAuthTokenGoogle = async ({ username, googleToken }) => {
  const clientId = 'USER_CLIENT_APP';
  const clientSecret = 'password';

  const body = new URLSearchParams({
    username,
    grant_type: 'google',
    google: googleToken,
  });

  const response = await fetch(`${apiOauthUrl}/oauth/token`, {
    method: 'POST',
    headers: {
      Authorization: 'Basic ' + btoa(`${clientId}:${clientSecret}`),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText);
  }

  return response.json();
};

export const createUserN = async payload => {
  const response = await fetch(`${apiOauthUrl}/POST/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Error al crear usuario');
  }

  return response.json();
};

export const fetchAllUsers = async () => {
  const response = await fetch(`${apiUrl}/GET/users`);
  if (!response.ok) {
    throw new Error('Error fetching users');
  }
  return response.json();
};

export const syncUserFromToken = async (dispatch, { silent = false } = {}) => {
  const token = localStorage.getItem('access_token');

  if (!token) {
    console.log('ℹ️ No token, skip sync');
    return false;
  }

  try {
    const response = await fetch(`${apiUrl}/my-profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error('Unauthorized');

    const raw = await response.json();
    const backendUser = Array.isArray(raw) ? raw[0] : raw;

    const normalizedUser = {
      user_id: backendUser.user_id,
      email: backendUser.user_email,
      name: backendUser.user_name,
      lastName: backendUser.user_lastname,
      google: false,
    };

    localStorage.setItem('user', JSON.stringify(normalizedUser));
    dispatch(setUser(normalizedUser));

    console.log('✅ Usuario sincronizado');
    return true;
  } catch (err) {
    if (!silent) {
      console.warn('🧹 Sesión inválida, limpiando storage');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      dispatch(setUser(null));
    } else {
      console.warn('⚠️ Sync falló, pero se conserva sesión local');
    }
    return false;
  }
};

export const fetchUserById = async id => {
  const response = await fetch(`${apiUrl}/GET/user/${id}`);
  if (!response.ok) {
    throw new Error('Error fetching user by ID');
  }
  return response.json();
};

export const createUser = async userData => {
  const response = await fetch(`${apiUrl}/POST/user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    throw new Error('Error creating user');
  }
  return response.json();
};

export const updateUserMainImage = async (userId, mainImageFiles) => {
  const formData = new FormData();
  for (const file of mainImageFiles) {
    formData.append('main_image', file);
  }
  formData.append('user_id', userId);
  for (const [key, value] of formData.entries()) {
    console.log(key, value);
  }

  const response = await fetch(`${apiUrl}/POST/user/update_main_image`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Error updating user main image');
  }
  return response.json();
};

export const updateUser = async ({ id, userData }) => {
  const response = await fetch(`${apiUrl}/PUT/user/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    throw new Error('Error updating user');
  }
  return response.json();
};

export const deleteUser = async id => {
  const response = await fetch(`${apiUrl}/PUT/delete_user/${id}`, {
    method: 'PUT',
  });

  if (!response.ok) {
    throw new Error('Error deleting user');
  }
  return response.json();
};

export const deleteUsersSeveral = async userIds => {
  const response = await fetch(`${apiUrl}/PUT/delete_user_several`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userIds }),
  });

  if (!response.ok) {
    throw new Error('Error deleting multiple users');
  }
  return response.json();
};

export const enabledUsersSeveral = async userIds => {
  const response = await fetch(`${apiUrl}/PUT/enabled_user_several`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userIds }),
  });

  if (!response.ok) {
    throw new Error('Error enabling multiple users');
  }
  return response.json();
};

export const disabledUsersSeveral = async userIds => {
  const response = await fetch(`${apiUrl}/PUT/disabled_user_several`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userIds }),
  });

  if (!response.ok) {
    throw new Error('Error disabling multiple users');
  }
  return response.json();
};

export const useFetchAllUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: fetchAllUsers,
  });
};

export const useFetchUserById = id => {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => fetchUserById(id),
    enabled: !!id,
  });
};

export const useCreateUser = () => {
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      console.log('User created successfully');
    },
    onError: error => {
      console.error('Error creating user:', error);
    },
  });
};

export const useUpdateUserMainImage = () => {
  return useMutation({
    mutationFn: data => updateUserMainImage(data.userId, data.mainImageFiles),
    onSuccess: () => {
      console.log('User main image updated successfully');
    },
    onError: error => {
      console.error('Error updating user main image:', error);
    },
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      console.log('User updated successfully');
    },
    onError: error => {
      console.error('Error updating user:', error);
    },
  });
};

export const useDeleteUser = () => {
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      console.log('User deleted successfully');
    },
    onError: error => {
      console.error('Error deleting user:', error);
    },
  });
};

export const useDeleteUsersSeveral = () => {
  return useMutation({
    mutationFn: deleteUsersSeveral,
    onSuccess: () => {
      console.log('Multiple users deleted successfully');
    },
    onError: error => {
      console.error('Error deleting multiple users:', error);
    },
  });
};

export const useEnabledUsersSeveral = () => {
  return useMutation({
    mutationFn: enabledUsersSeveral,
    onSuccess: () => {
      console.log('Multiple users enabled successfully');
    },
    onError: error => {
      console.error('Error enabling multiple users:', error);
    },
  });
};

export const useDisabledUsersSeveral = () => {
  return useMutation({
    mutationFn: disabledUsersSeveral,
    onSuccess: () => {
      console.log('Multiple users disabled successfully');
    },
    onError: error => {
      console.error('Error disabling multiple users:', error);
    },
  });
};
